<template>
  <section id="troubleshooting" class="ts-container">
    <h2>Troubleshooting</h2>
    <p class="section-desc">
      프로젝트를 진행하며 마주쳤던 문제들과 해결 과정을 기록했습니다.<br />
      문제의 원인을 깊이 파고들어 더 나은 코드로 개선하는 것을 즐깁니다.
    </p>
    <div class="ts-accordion">
      <div
        v-for="(item, index) in troubleshootingCases"
        :key="index"
        class="ts-item"
        :class="{ active: activeIndex === index }"
        @click="toggleAccordion(index)"
      >
        <div class="ts-header">
          <h3>{{ item.title }}</h3>
          <span class="toggle-icon">{{
            activeIndex === index ? " " : "+"
          }}</span>
        </div>
        <div class="ts-content-wrapper">
          <div class="ts-content">
            <div class="ts-keywords">
              <span
                v-for="keyword in item.keywords"
                :key="keyword"
                class="ts-keyword"
                >#{{ keyword }}</span
              >
            </div>
            <div class="ts-detail">
              <h4>💡 문제 상황 (Problem)</h4>
              <p>{{ item.problem }}</p>
            </div>
            <div class="ts-detail">
              <h4>🛠️ 문제 해결 (Troubleshooting)</h4>
              <p>{{ item.solution }}</p>
            </div>
            <div class="ts-detail">
              <h4>✅ 결과 및 배운 점 (Result & Learnings)</h4>
              <p>{{ item.learnings }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "TroubleshootingSection",
  data() {
    return {
      activeIndex: null,
      troubleshootingCases: [
        {
          title: "CPU 크레딧 소진으로 인한 AWS EC2 장애 분석 및 대응",
          keywords: [
            "장애 대응",
            "인프라 모니터링",
            "AWS",
            "Root Cause Analysis",
          ],
          problem:
            "평소 정상 작동하던 서비스가 갑자기 응답 불능 상태에 빠지며 SSH 접속조차 불가한 '먹통' 현상이 발생했습니다. 이는 단순한 애플리케이션 오류가 아닌, 서비스 전체를 마비시킬 수 있는 심각한 인프라 레벨의 장애 상황이었습니다.",
          solution:
            "단순 네트워크 문제가 아님을 직감하고 CloudWatch 지표를 통해 근본 원인(Root Cause) 분석을 시작했습니다. CPU 사용률이 100%에 고정된 것을 확인, T3 인스턴스의 'CPU 크레딧'이 모두 소진되어 성능이 강제로 제한된 상태임을 파악했습니다. 즉각적인 서비스 복구를 위해 인스턴스를 재부팅하여 크레딧을 확보하고, 장애 시간 동안 중단된 Docker 컨테이너들을 재실행하여 서비스를 정상화했습니다.",
          learnings:
            "비용 효율적인 인프라 선택의 중요성과 그에 따르는 책임의 무게를 체감했습니다. 단기적으로는 서비스를 복구했지만, 근본적인 해결을 위해 장기적인 인프라 개선안(T3 Unlimited 모드 활성화, 임계치 기반의 CloudWatch 경보 설정)을 수립하며 장애를 예방하는 시스템을 구축하는 경험을 쌓았습니다.",
          link: "https://your-blog.com/aws-ec2-ssh-troubleshooting",
        },
        {
          title:
            "결제 시스템의 금액 위변조 취약점 발견 및 서버 사이드 검증 강화",
          keywords: ["보안 취약점", "서버 사이드 검증", "API 설계", "IAMPORT"],
          problem:
            "IAMPORT 결제 연동 플로우를 설계하던 중, 클라이언트에서 결제 금액을 조작하여 요청할 경우 금전적 손실을 야기할 수 있는 치명적인 보안 허점을 발견했습니다. 이는 서비스의 신뢰도와 직결되는 매우 중요한 문제였습니다.",
          solution:
            "'절대 클라이언트를 신뢰해서는 안된다'는 보안 원칙을 적용하여, 서버 주도의 교차 검증 로직을 설계했습니다. 클라이언트가 전달한 `imp_uid`를 사용해 서버가 직접 IAMPORT API에 결제 정보를 조회하고, 우리 DB에 기록된 주문 금액과 일치하는지 확인하는 단계를 추가했습니다. 금액이 단 1원이라도 다를 경우, 결제를 즉시 취소하고 비정상 접근으로 기록하도록 구현했습니다.",
          learnings:
            "백엔드 개발자의 코드는 단순한 기능 구현을 넘어 서비스의 안정성과 신뢰도를 책임져야 함을 깨달았습니다. 눈에 보이지 않는 잠재적 위협까지 예측하고 방어 로직을 설계하는 것이 견고한 백엔드 시스템의 핵심이라는 저만의 엔지니어링 원칙을 세우게 된 계기가 되었습니다.",
          link: "https://your-blog.com/iamport-payment-validation",
        },
        {
          title: "Redis Queue의 한계 분석 및 Sorted Set을 통한 성능 개선",
          keywords: ["성능 개선", "데이터 정합성", "Redis", "자료구조"],
          problem:
            "최근 검색어 기능을 List(Queue)로 구현한 결과, 중복 데이터가 계속 쌓이고 최신순 정렬을 위한 별도의 로직이 필요해 불필요한 연산이 발생했습니다. 이는 데이터 정합성을 해치고 API 응답 시간을 지연시키는 기술 부채로 작용했습니다.",
          solution:
            "문제의 본질이 '기능의 부재'가 아닌 '잘못된 도구의 사용'에 있다고 판단, Redis의 Sorted Set으로 구조 변경을 결정했습니다. 시간(timestamp)을 `score`로 활용하여 데이터 삽입과 동시에 자동 정렬이 이루어지게 하고, 중복된 값은 score만 업데이트 되도록 하여 데이터의 유일성을 보장했습니다. 이를 통해 복잡했던 애플리케이션 레벨의 정렬 및 중복 제거 로직을 모두 제거하고 코드를 단순화했습니다.",
          learnings:
            "데이터의 특성과 비즈니스 요구사항을 깊이 이해하고 그에 맞는 최적의 자료구조를 선택하는 것이 최고의 성능 튜닝임을 배웠습니다. 이 경험을 통해 기술을 ‘사용하는 것’을 넘어, ‘제대로 활용하는 것’의 중요성을 체감했습니다.",
          link: "https://your-blog.com/redis-sorted-set-refactoring",
        },
        {
          title: "JPQL의 런타임 에러 리스크와 QueryDSL을 통한 코드 안정성 확보",
          keywords: ["코드 안정성", "유지보수", "QueryDSL", "타입 세이프"],
          problem:
            "복잡한 동적 쿼리를 JPQL 문자열 조합으로 구현하면서, 사소한 오타 하나가 컴파일 시점에는 잡히지 않고 런타임에 가서야 터지는 위험천만한 상황을 마주했습니다. 이는 안정적인 서비스 운영에 큰 위협이었고, 유지보수 비용을 증가시키는 기술 부채였습니다.",
          solution:
            "이러한 잠재적 위험을 근본적으로 차단하고자 QueryDSL을 전면 도입했습니다. 모든 JPQL 쿼리를 타입 세이프(Type-safe)한 Java 코드로 전환하여, 쿼리의 모든 요소가 컴파일 시점에 검증되도록 만들었습니다. 이를 통해 런타임 에러 가능성을 원천적으로 차단하고, 동적 쿼리 또한 메서드 체이닝을 통해 훨씬 직관적이고 가독성 높게 개선했습니다.",
          learnings:
            "'안전한 실패(Fail-safe)'를 지향하는 코드의 가치를 깨달았습니다. 당장의 개발 속도보다 장기적인 안정성과 유지보수성을 고려한 기술 선택이 결국 더 빠른 길이라는 것을 배웠고, 견고한 애플리케이션의 토대를 다지는 경험을 했습니다.",
          link: "https://your-blog.com/querydsl-refactoring",
        },
      ],
    };
  },
  methods: {
    toggleAccordion(index) {
      this.activeIndex = this.activeIndex === index ? null : index;
    },
  },
};
</script>
<style scoped>
.ts-container {
  padding: 100px 5%;
  background-color: var(--bg-secondary-color);
}
h2 {
  font-size: 2.5rem;
  margin-bottom: 20px;
  text-align: center;
}
.section-desc {
  font-size: 1.1rem;
  color: var(--text-secondary-color);
  max-width: 700px;
  margin: 0 auto 50px auto;
  line-height: 1.7;
  text-align: center;
}
.ts-accordion {
  max-width: 900px;
  margin: 0 auto;
}
.ts-item {
  background: var(--bg-color);
  border-radius: 15px;
  border: 1px solid var(--card-border-color);
  margin-bottom: 15px;
  overflow: hidden;
  transition: all 0.4s ease-in-out;
}
.ts-item.active {
  border-color: var(--point-color);
  box-shadow: 0 8px 30px var(--card-shadow-color);
}
.ts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  cursor: pointer;
}
.ts-header h3 {
  font-size: 1.3rem;
  margin: 0;
  color: var(--text-primary-color);
  font-family: "Fira Code", monospace;
}
.toggle-icon {
  font-size: 2rem;
  color: var(--text-secondary-color);
  transition: transform 0.3s ease;
}
.ts-item.active .toggle-icon {
  transform: rotate(45deg);
  color: var(--point-color);
}
.ts-content-wrapper {
  max-height: 0;
  transition: max-height 0.5s ease-in-out;
}
.ts-item.active .ts-content-wrapper {
  max-height: 1000px;
}
.ts-content {
  padding: 0 30px 30px 30px;
  border-top: 1px solid var(--card-border-color);
}
.ts-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
  margin-bottom: 25px;
}
.ts-keyword {
  background-color: var(--tag-bg-color);
  color: var(--text-secondary-color);
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-family: "Fira Code", monospace;
}
.ts-detail {
  margin-bottom: 20px;
}
.ts-detail h4 {
  font-size: 1rem;
  color: var(--text-secondary-color);
  margin: 0 0 8px 0;
}
.ts-detail p {
  font-size: 0.95rem;
  color: var(--text-primary-color);
  margin: 0;
  line-height: 1.7;
}
.ts-footer {
  margin-top: 20px;
  text-align: right;
}
.link-btn {
  color: var(--point-color);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}
.link-btn:hover {
  filter: brightness(0.9);
}

@media (max-width: 768px) {
  .ts-container {
    padding: 60px 5%;
  }
}
</style>
